import { lazy } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

// Auth Guard
const AuthGuard = lazy(() => import("./components/AuthGuard"));

// Pages
const Login = lazy(() => import("./pages/Login"));
const NotFound = lazy(() => import("./pages/NotFound"));

import DashboardGeralV2 from "./pages/DashboardGeralV2";
const BIEstrategico = lazy(() => import("./pages/bi/BIEstrategico"));
const BIOperacional = lazy(() => import("./pages/bi/BIOperacional"));
const UnifiedProcessesV2 = lazy(() => import("./pages/UnifiedProcessesV2"));
const AlertasV2 = lazy(() => import("./pages/AlertasV2"));
const ExtensaoINPI = lazy(() => import("./pages/geral/ExtensaoINPI"));

const DashboardComercialV2 = lazy(() => import("./pages/DashboardComercialV2"));
const CRMKanban = lazy(() => import("./pages/CRMKanban"));
const OportunidadesV2 = lazy(() => import("./pages/OportunidadesV2"));
const OportunidadeDetalheV2 = lazy(() => import("./pages/OportunidadeDetalheV2"));
const ClientesV2 = lazy(() => import("./pages/ClientesV2"));

const FinanceiroV2 = lazy(() => import("./pages/FinanceiroV2"));

const DashboardCadastrosV2 = lazy(() => import("./pages/DashboardCadastrosV2"));
const CadastrosServicosV2 = lazy(() => import("./pages/CadastrosServicosV2"));
const ClienteDetalheV2 = lazy(() => import("./pages/ClienteDetalheV2"));
const EmailTemplatesV2 = lazy(() => import("./pages/EmailTemplatesV2"));

const DashboardMarcasV2 = lazy(() => import("./pages/DashboardMarcasV2"));
const ExtrasMarcasV2 = lazy(() => import("./pages/ExtrasMarcasV2"));
const BuscaGlobalMarcas = lazy(() => import("./pages/marcas/BuscaGlobalMarcas"));
const MeusProcessosMarcas = lazy(() => import("./pages/marcas/MeusProcessosMarcas"));
const MonitorMarcas = lazy(() => import("./pages/marcas/MonitorMarcas"));

const DashboardPatentesV2 = lazy(() => import("./pages/DashboardPatentesV2"));
const BuscaGlobalPatentes = lazy(() => import("./pages/patentes/BuscaGlobalPatentes"));
const MeusProcessosPatentes = lazy(() => import("./pages/patentes/MeusProcessosPatentes"));
const PatentMonitoringHubV2 = lazy(() => import("./pages/patentes/PatentMonitoringHubV2"));
const CollisionReportPage = lazy(() => import("./pages/patentes/CollisionReportPage"));
const NipPatSearch = lazy(() => import("./pages/patentes/NipPatSearch"));
const NipWriter = lazy(() => import("./pages/patentes/NipWriter"));
const EmailClientePatentes = lazy(() => import("./pages/patentes/EmailClientePatentes"));
const PatenteDetalhe = lazy(() => import("./pages/patentes/PatenteDetalhe"));
const NewResearch = lazy(() => import("./pages/NewResearch"));
const ResearchHistory = lazy(() => import("./pages/ResearchHistory"));
const Transcription = lazy(() => import("./pages/Transcription"));
const StructuredBriefing = lazy(() => import("./pages/StructuredBriefing"));
const Keywords = lazy(() => import("./pages/Keywords"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const Analysis = lazy(() => import("./pages/Analysis"));
const Report = lazy(() => import("./pages/Report"));
const ResearchSettings = lazy(() => import("./pages/ResearchSettings"));

const DashboardDIV2 = lazy(() => import("./pages/DashboardDIV2"));
const ExtrasDIV2 = lazy(() => import("./pages/ExtrasDIV2"));
const BuscaGlobalDI = lazy(() => import("./pages/di/BuscaGlobalDI"));
const MeusProcessosDI = lazy(() => import("./pages/di/MeusProcessosDI"));
const MonitorDI = lazy(() => import("./pages/di/MonitorDI"));

const DashboardSoftwareV2 = lazy(() => import("./pages/DashboardSoftwareV2"));

const DashboardOperacionalV2 = lazy(() => import("./pages/operations/DashboardOperacionalV2"));
const DemandsV2 = lazy(() => import("./pages/DemandsV2"));
const DemandDetailV2 = lazy(() => import("./pages/DemandDetailV2"));

function PreserveQueryNavigate({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

function PreserveAtendimentoDetailNavigate() {
  const { id = "" } = useParams();
  const location = useLocation();
  return <Navigate to={`/comercial/atendimentos/${id}${location.search}`} replace />;
}

const DashboardInternacionalV2 = lazy(() => import("./pages/DashboardInternacionalV2"));

const DashboardAutoralV2 = lazy(() => import("./pages/DashboardAutoralV2"));

const MyResponsibilitiesV2 = lazy(() => import("./pages/MyResponsibilitiesV2"));
const ProcessosV2 = lazy(() => import("./pages/ProcessosV2"));
const ProcessoDetalheV2 = lazy(() => import("./pages/ProcessoDetalheV2"));
const ModuleAcervoV2 = lazy(() => import("./pages/ModuleAcervoV2"));

const QuickSearch = lazy(() => import("./pages/QuickSearch"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const Proposal = lazy(() => import("./pages/Proposal"));
const MyAssets = lazy(() => import("./pages/MyAssets"));
const MonitoringSettings = lazy(() => import("./pages/MonitoringSettings"));
const DispatchTemplates = lazy(() => import("./pages/DispatchTemplates"));

const BackgroundWorkers = lazy(() => import("./pages/BackgroundWorkers"));
const PipelineMonitor = lazy(() => import("./pages/PipelineMonitor"));
const CollisionMonitor = lazy(() => import("./pages/CollisionMonitor"));
const SystemHealthV2 = lazy(() => import("./pages/operations/SystemHealthV2"));
const Settings = lazy(() => import("./pages/Settings"));
const SuperAdminHome = lazy(() => import("./pages/admin/SuperAdminHome"));
const SuperAdminTenants = lazy(() => import("./pages/admin/SuperAdminTenants"));
const SuperAdminUsers = lazy(() => import("./pages/admin/SuperAdminUsers"));
const SuperAdminApiKeys = lazy(() => import("./pages/admin/SuperAdminApiKeys"));

const DispatchCodesV2 = lazy(() => import("./pages/settings/DispatchCodesV2"));
const AutomacoesV2 = lazy(() => import("./pages/settings/AutomacoesV2"));
const MonitoringMarketV2 = lazy(() => import("./pages/monitoring/MonitoringMarketV2"));
const MonitoringGroupsV2 = lazy(() => import("./pages/monitoring/MonitoringGroupsV2"));

// Módulo de automações de eventos (/v2/automations) — antes só backend, sem UI.
const AutomacoesEventos = lazy(() => import("./pages/AutomacoesEventos"));

export const appRoutes = [
  // Geral
  { path: "/geral", element: <DashboardGeralV2 /> },
  { path: "/geral/bi-estrategico", element: <BIEstrategico /> },
  { path: "/geral/bi-operacional", element: <BIOperacional /> },
  { path: "/geral/extensao", element: <ExtensaoINPI /> },
  { path: "/bi/estrategico", element: <Navigate to="/geral/bi-estrategico" replace /> },
  { path: "/bi/operacional", element: <Navigate to="/geral/bi-operacional" replace /> },
  { path: "/executivo", element: <Navigate to="/geral" replace /> },
  { path: "/gerente", element: <Navigate to="/geral/bi-estrategico" replace /> },
  { path: "/processos-unificados", element: <UnifiedProcessesV2 /> },
  { path: "/meus-processos", element: <Navigate to="/processos-unificados" replace /> },
  { path: "/alerts", element: <AlertasV2 /> },

  // Comercial
  { path: "/comercial", element: <DashboardComercialV2 /> },
  { path: "/comercial/kanban", element: <CRMKanban /> },
  { path: "/comercial/atendimentos", element: <OportunidadesV2 /> },
  { path: "/comercial/atendimentos/:id", element: <OportunidadeDetalheV2 /> },
  { path: "/comercial/oportunidades", element: <PreserveQueryNavigate to="/comercial/atendimentos" /> },
  { path: "/comercial/oportunidades/:id", element: <PreserveAtendimentoDetailNavigate /> },
  { path: "/comercial/clientes", element: <ClientesV2 /> },

  // Financeiro
  { path: "/financeiro", element: <FinanceiroV2 /> },

  // Cadastros
  { path: "/cadastros", element: <DashboardCadastrosV2 /> },
  { path: "/cadastros/clientes/:id", element: <ClienteDetalheV2 /> },
  { path: "/cadastros/servicos", element: <CadastrosServicosV2 /> },
  { path: "/cadastros/templates-email", element: <EmailTemplatesV2 /> },

  // Marcas
  { path: "/marcas", element: <DashboardMarcasV2 /> },
  { path: "/marcas/busca-global", element: <BuscaGlobalMarcas /> },
  { path: "/marcas/busca-inpi", element: <BuscaGlobalMarcas /> },
  { path: "/marcas/meus-processos", element: <MeusProcessosMarcas /> },
  { path: "/marcas/processos", element: <Navigate to="/marcas/meus-processos" replace /> },
  { path: "/marcas/monitor", element: <MonitorMarcas /> },
  { path: "/marcas/nip-search", element: <Navigate to="/marcas/busca-global" replace /> },
  { path: "/marcas/nip-apply", element: <Navigate to="/marcas/meus-processos" replace /> },
  { path: "/marcas/internacional", element: <Navigate to="/internacional" replace /> },
  { path: "/marcas/email", element: <EmailTemplatesV2 /> },
  { path: "/marcas/extras", element: <ExtrasMarcasV2 /> },
  { path: "/marcas/meus", element: <PreserveQueryNavigate to="/marcas/meus-processos" /> },
  { path: "/marcas/pesquisa", element: <Navigate to="/marcas/busca-global" replace /> },
  { path: "/marcas/operacional", element: <Navigate to="/marcas/meus-processos" replace /> },
  { path: "/marcas/colidencias", element: <Navigate to="/marcas/monitor" replace /> },
  { path: "/marcas/alertas", element: <Navigate to="/marcas/monitor" replace /> },
  { path: "/marcas/acervo", element: <Navigate to="/marcas/meus-processos" replace /> },

  // Patentes
  { path: "/patentes", element: <DashboardPatentesV2 /> },
  { path: "/patentes/busca", element: <BuscaGlobalPatentes /> },
  { path: "/patentes/meus", element: <MeusProcessosPatentes /> },
  { path: "/patentes/meus/:patentId", element: <PatenteDetalhe /> },
  { path: "/patentes/monitor", element: <PatentMonitoringHubV2 /> },
  { path: "/patentes/monitor/colidencia/:id", element: <CollisionReportPage /> },
  { path: "/patentes/nip-search", element: <NipPatSearch /> },
  { path: "/patentes/redacao", element: <NipWriter /> },
  { path: "/patentes/internacional", element: <DashboardInternacionalV2 /> },
  { path: "/patentes/email", element: <EmailClientePatentes /> },
  { path: "/patentes/historico", element: <ResearchHistory /> },
  { path: "/patentes/pesquisa", element: <Navigate to="/patentes/busca" replace /> },
  { path: "/patentes/busca-global", element: <Navigate to="/patentes/busca" replace /> },
  { path: "/patentes/processos", element: <PreserveQueryNavigate to="/patentes/meus" /> },
  { path: "/patentes/nip-writer", element: <Navigate to="/patentes/redacao" replace /> },
  { path: "/patentes/operacional", element: <Navigate to="/patentes/monitor" replace /> },
  { path: "/patentes/alertas", element: <Navigate to="/patentes/monitor" replace /> },
  { path: "/patentes/base-local", element: <Navigate to="/patentes/busca" replace /> },
  { path: "/patentes/acervo", element: <Navigate to="/patentes/busca" replace /> },

  // DI
  { path: "/di", element: <DashboardDIV2 /> },
  { path: "/di/busca-inpi", element: <BuscaGlobalDI /> },
  { path: "/di/nip-search", element: <Navigate to="/di/busca-inpi" replace /> },
  { path: "/di/meus-processos", element: <MeusProcessosDI /> },
  { path: "/di/processos", element: <Navigate to="/di/meus-processos" replace /> },
  { path: "/di/monitor", element: <MonitorDI /> },
  { path: "/di/nip-apply", element: <Navigate to="/di/meus-processos" replace /> },
  { path: "/di/internacional", element: <Navigate to="/internacional" replace /> },
  { path: "/di/email", element: <EmailTemplatesV2 /> },
  { path: "/di/extras", element: <ExtrasDIV2 /> },
  { path: "/di/meus", element: <PreserveQueryNavigate to="/di/meus-processos" /> },
  { path: "/di/colidencias", element: <Navigate to="/di/monitor" replace /> },
  { path: "/di/operacional", element: <Navigate to="/di/meus-processos" replace /> },
  { path: "/di/alertas", element: <Navigate to="/di/monitor" replace /> },
  { path: "/di/acervo", element: <Navigate to="/di/meus-processos" replace /> },

  // Software
  { path: "/software", element: <DashboardSoftwareV2 /> },
  { path: "/software/meus", element: <MyResponsibilitiesV2 /> },
  { path: "/software/operacional", element: <ProcessosV2 /> },
  { path: "/software/acervo", element: <ModuleAcervoV2 /> },

  // Autoral
  { path: "/autoral", element: <DashboardAutoralV2 /> },
  { path: "/autoral/obras", element: <MyResponsibilitiesV2 /> },
  { path: "/autoral/operacional", element: <ProcessosV2 /> },
  { path: "/autoral/alertas", element: <AlertasV2 /> },

  // Operacional
  { path: "/operacional", element: <DashboardOperacionalV2 /> },
  { path: "/operacional/kanban", element: <DemandsV2 /> },
  { path: "/operacional/lista", element: <DemandsV2 /> },
  { path: "/operacional/demandas", element: <Navigate to="/operacional/lista" replace /> },
  { path: "/operacional/demandas/nova", element: <DemandDetailV2 /> },
  { path: "/operacional/demandas/:id", element: <DemandDetailV2 /> },

  // Internacional
  { path: "/internacional", element: <DashboardInternacionalV2 /> },
  { path: "/internacional/operacional", element: <ProcessosV2 /> },
  { path: "/internacional/alertas", element: <AlertasV2 /> },

  // Processos
  { path: "/processos", element: <ProcessosV2 /> },
  { path: "/processo/:id", element: <ProcessoDetalheV2 /> },

  // Research
  { path: "/research/new", element: <NewResearch /> },
  { path: "/research/briefing", element: <NewResearch /> },
  { path: "/research/transcription", element: <Transcription /> },
  { path: "/research/structured", element: <StructuredBriefing /> },
  { path: "/research/keywords", element: <Keywords /> },
  { path: "/research/results", element: <SearchResults /> },
  { path: "/research/analysis", element: <Analysis /> },
  { path: "/research/report", element: <Report /> },
  { path: "/research/history", element: <ResearchHistory /> },
  { path: "/research/settings", element: <ResearchSettings /> },

  // Misc
  { path: "/search", element: <QuickSearch /> },
  { path: "/knowledge-base", element: <KnowledgeBase /> },
  { path: "/proposta", element: <Proposal /> },

  // Admin
  // Super Admin (platform_superadmin → menuProfile "platform")
  { path: "/platform", element: <AuthGuard allowedMenuProfiles={["platform"]}><SuperAdminHome /></AuthGuard> },
  { path: "/platform/tenants", element: <AuthGuard allowedMenuProfiles={["platform"]}><SuperAdminTenants /></AuthGuard> },
  { path: "/platform/users", element: <AuthGuard allowedMenuProfiles={["platform"]}><SuperAdminUsers /></AuthGuard> },
  { path: "/platform/api-keys", element: <AuthGuard allowedMenuProfiles={["platform"]}><SuperAdminApiKeys /></AuthGuard> },
  { path: "/operations/workers", element: <BackgroundWorkers /> },
  { path: "/operations/pipeline", element: <PipelineMonitor /> },
  { path: "/operations/collisions", element: <CollisionMonitor /> },
  { path: "/operations/system-health", element: <SystemHealthV2 /> },
  { path: "/settings", element: <Settings /> },
  { path: "/settings/dispatch-codes", element: <DispatchCodesV2 /> },
  { path: "/settings/automacoes", element: <AutomacoesV2 /> },
  { path: "/automacoes", element: <AutomacoesEventos /> },
  { path: "/crm/atendimentos", element: <PreserveQueryNavigate to="/comercial/atendimentos" /> },
  { path: "/monitoring/mercado", element: <MonitoringMarketV2 /> },
  { path: "/monitoring/grupos", element: <MonitoringGroupsV2 /> },

  // Legacy
  { path: "/dashboard", element: <Navigate to="/geral" replace /> },
  { path: "/clients", element: <Navigate to="/comercial/clientes" replace /> },
  { path: "/demands", element: <Navigate to="/operacional/lista" replace /> },
  { path: "/monitoring", element: <Navigate to="/patentes/monitor" replace /> },
  { path: "/monitoring/dashboard", element: <Navigate to="/patentes" replace /> },
  { path: "/monitoring/collision", element: <Navigate to="/patentes/monitor" replace /> },
  { path: "/monitoring/process", element: <Navigate to="/di/meus-processos" replace /> },
  { path: "/monitoring/market", element: <Navigate to="/internacional" replace /> },
  { path: "/monitoring/patents", element: <Navigate to="/patentes/processos" replace /> },
  { path: "/monitoring/base", element: <Navigate to="/patentes/acervo" replace /> },
  { path: "/monitoring/background-workers", element: <Navigate to="/operations/workers" replace /> },
  { path: "/monitoring/settings", element: <MonitoringSettings /> },
  { path: "/configuracoes/gatilhos", element: <DispatchTemplates /> },
  { path: "/monitoring/assets", element: <MyAssets /> },
  { path: "/base/patents", element: <Navigate to="/patentes/acervo" replace /> },
  { path: "/research/dashboard", element: <Navigate to="/geral" replace /> },
  { path: "*", element: <NotFound /> },
];
